import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AppStateService } from '../../services/app-state.service';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import {Observable, of, take} from 'rxjs';
import { LoadingSpinnerComponent } from './loading-spinner-component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {AppState} from '../../models/AppState';

@Component({
  selector: 'app-loyalty-agreement',
  standalone: true,
  imports: [
    CommonModule,
    LoadingSpinnerComponent
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="text-theme-text text-base">
      <div *ngIf="isLoading" class="flex justify-center items-center py-8">
        <app-loading-spinner size="lg"></app-loading-spinner>
      </div>

      <div *ngIf="!isLoading && errorMessage" class="text-red-500 py-4 text-center">
        {{ errorMessage }}
        <div class="mt-4">
          <button (click)="loadAgreement()" class="px-4 py-2 bg-theme-primary text-theme-contrast rounded-lg">
            Försök igen
          </button>
        </div>
      </div>

      <div *ngIf="!isLoading && !errorMessage" class="agreement-content">
        <div [innerHTML]="agreementContent"></div>
      </div>
    </div>
  `,
  styles: [`
    .agreement-content {
    }

    .agreement-content h1 {
      @apply text-2xl font-bold mt-6 mb-3 text-theme-text;
    }

    .agreement-content h2 {
      @apply text-xl font-bold mt-4 mb-2 text-theme-text;
    }

    .agreement-content p {
      @apply text-theme-text2 mb-4 leading-relaxed;
    }

    .agreement-content ul {
      @apply list-disc ml-6 mb-4;
    }

    .agreement-content ol {
      @apply list-decimal ml-6 mb-4;
    }

    .agreement-content li {
      @apply mb-2 text-theme-text2;
    }
  `]
})
export class LoyaltyAgreementComponent implements OnInit {
  agreementContent: SafeHtml | null = null;
  isLoading: boolean = false;
  errorMessage: string | null = null;

  private readonly AGREEMENT_URL = 'https://storage.googleapis.com/heynowdocs/agreement/loyalty.html';
  private appState?: AppState;

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.appStateService.observeAppState().pipe(take(1),).subscribe((appState) => {
      this.appState = appState;
      this.loadAgreement();
    });

  }

  loadAgreement(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.fetchAgreementHtml()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (content) => {
          this.agreementContent = content;
        },
        error: (error) => {
          console.error('Error loading loyalty agreement:', error);
          this.errorMessage = 'Kunde inte ladda användarvillkoren. Försök igen senare.';
        }
      });
  }

  private fetchAgreementHtml(): Observable<SafeHtml> {
    return this.http.get(this.AGREEMENT_URL, { responseType: 'text' })
      .pipe(
        map(html => {
          let processedHtml = html.replace(new RegExp('{{RESTAURANGNAMN}}', 'g'), this.appState?.cfg.name ?? "Restaurangen");
          processedHtml = processedHtml.replace(new RegExp('{{ORGANISATIONSNUMMER}}', 'g'), this.appState?.cfg.org_number ?? "-");
          processedHtml = processedHtml.replace(new RegExp('{{ADRESS}}', 'g'), this.appState?.cfg.bus_address ?? "-");
          processedHtml = processedHtml.replace(new RegExp('{{E-POST}}', 'g'), this.appState?.cfg.email ?? "-");
          processedHtml = processedHtml.replace(new RegExp('{{TELEFONNUMMER}}', 'g'), this.appState?.cfg.phone ?? "-");

          // Sanitize HTML to prevent XSS attacks but allow HTML rendering
          return this.sanitizer.bypassSecurityTrustHtml(processedHtml);
        }),
        catchError(error => {
          console.error('Error fetching agreement HTML:', error);
          throw error;
        })
      );
  }

}
